import React, { useEffect, useMemo, useState } from 'react';
import { ExternalLink, Search, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import V2Shell from './V2Shell';

// Orders board, merged from hsy's Order Dashboard / Customer Order Zone / Cleanup.
const API = 'api/replai/v2/orders/';
const ODOO = 'https://lamptwist.odoo.com/odoo/sales/';
const GORGIAS = 'https://lamptwist.gorgias.com/app/ticket/';
const SHOPIFY = 'https://admin.shopify.com/store/lamptwist-live/orders/';
const ISSUE = { 'Overdue delivery': 'bg-red-100 text-red-800', 'Waiting on brand (>14d)': 'bg-amber-100 text-amber-800' };

const Chip = ({ active, onClick, children }) => (
  <button type="button" onClick={onClick}
    className={`rounded-md px-3 py-1.5 text-sm ${active ? 'bg-[var(--ink)] text-white' : 'text-[var(--ink-2)] hover:bg-[var(--line-2)]'}`}>
    {children}
  </button>
);

const money = (v, c) => (v === '' || v == null ? '–' : `${Number(v).toLocaleString('en-GB', { maximumFractionDigits: 2 })} ${c || ''}`);

const Links = ({ o, className = '' }) => (
  <span className={`inline-flex flex-wrap gap-x-3 gap-y-1 text-xs ${className}`} onClick={(e) => e.stopPropagation()}>
    {o.order_id && <a href={`${ODOO}${o.order_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 hover:underline">Odoo <ExternalLink size={11} /></a>}
    {o.shopify_order_id && <a href={`${SHOPIFY}${o.shopify_order_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 hover:underline">Shopify <ExternalLink size={11} /></a>}
    {o.ticket_id && <a href={`${GORGIAS}${o.ticket_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 hover:underline">Ticket <ExternalLink size={11} /></a>}
  </span>
);

// The order as Replai's agent sees it: status, money, every line with what Odoo
// has reserved and the timing it would tell the customer, and each shipment
// (Geoff, 2026-10-20: show order lines, statuses, tracking).
const PICK = { done: 'bg-green-100 text-green-800', assigned: 'bg-blue-100 text-blue-800', waiting: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-amber-100 text-amber-800', cancel: 'bg-red-100 text-red-800' };
const PICK_LABEL = { done: 'shipped', assigned: 'ready to ship', waiting: 'waiting for goods', confirmed: 'waiting for goods', cancel: 'cancelled', draft: 'draft' };
const lineTone = (st = '') => (/ship|deliver/i.test(st) ? 'bg-green-100 text-green-800' : /later|late|cancel/i.test(st) ? 'bg-red-100 text-red-800'
  : /warehouse|stock/i.test(st) ? 'bg-blue-100 text-blue-800' : 'bg-[var(--line-2)] text-[var(--ink-2)]');
const Pill = ({ tone, children }) => <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>{children}</span>;
const Fact = ({ label, children, tone = '' }) => (children == null || children === '' || children === false ? null : (
  <div><dt className="text-[0.7rem] uppercase tracking-wide text-[var(--ink-3)]">{label}</dt><dd className={`text-sm ${tone}`}>{children}</dd></div>));
const qty = (n) => (n == null ? '' : Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 }));

const OrderModal = ({ order, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    axiosInstance.get(API, { params: { view: 'detail', order: order.order_name } }).then(({ data }) => setD(data)).catch(() => setD(false));
    const esc = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [order.order_name, onClose]);
  const o = d || order;
  const ship = d?.shipment || {};
  const parcels = ship.carrier_status?.parcels || [];
  const email = o.email || o.l_email;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/30 p-4 sm:p-10" onClick={onClose} role="dialog" aria-modal="true">
      <div className="w-full max-w-4xl rounded-xl bg-[var(--surface)] p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-semibold">{o.order_name}</h2>
              {(o.order_status || o.status) && <Pill tone={lineTone(o.order_status || o.status)}>{o.order_status || o.status}</Pill>}
              {o.payment && <Pill tone={/paid/i.test(o.payment) && !/un|not|partial/i.test(o.payment) ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>{o.payment}</Pill>}
              {ship.shopify_status && <Pill tone="bg-red-100 text-red-800">Shopify: {ship.shopify_status}</Pill>}
            </div>
            <div className="mt-1 text-sm text-[var(--ink-2)]">
              {(o.order_date || '').slice(0, 10)}
              {email && <> · <Link to={`/customers?email=${encodeURIComponent(email)}`} className="hover:underline">{email}</Link></>}
              {o.country ? ` · ships to ${o.country}` : ''}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
              <Links o={{ ...order, ...(d || {}) }} />
              {d?.invoice_url && <a href={d.invoice_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-xs hover:underline">Invoice <ExternalLink size={11} /></a>}
            </div>
          </div>
          <div className="flex items-start gap-3">
            {o.total ? (
              <div className="text-right text-sm">
                <div className="num text-lg font-semibold">{money(o.total, o.currency)}</div>
                {d?.shipping_value != null && <div className="num text-xs text-[var(--ink-3)]">{money(d.products_value, o.currency)} items · {money(d.shipping_value, o.currency)} shipping</div>}
              </div>) : null}
            <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1 text-[var(--ink-2)] hover:bg-[var(--paper)]"><X size={18} /></button>
          </div>
        </div>
        {d == null && <div className="mt-4 h-32 animate-pulse rounded bg-[var(--line-2)]" />}
        {d === false && <p className="mt-4 text-sm text-red-700">Could not load this order.</p>}
        {d && (
          <>
            {d.note && <p className="mt-3 rounded-md bg-[var(--paper)] p-2 text-xs text-[var(--ink-2)]"><b>Order note:</b> {d.note}</p>}
            <h3 className="mt-5 text-xs font-semibold uppercase tracking-wide text-[var(--ink-3)]">Items ({d.lines.length})</h3>
            <ul className="mt-2 space-y-2">
              {d.lines.map((l, i) => (
                <li key={i} className="flex gap-3 rounded-lg border border-[var(--line)] p-3 text-sm">
                  {l.l_image_url ? <img src={l.l_image_url} alt="" className="h-16 w-16 flex-none rounded-md bg-[var(--paper)] object-contain" loading="lazy" />
                    : <div className="h-16 w-16 flex-none rounded-md bg-[var(--paper)]" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <span className="font-medium">
                        {l.l_variant_url ? <a href={l.l_variant_url} target="_blank" rel="noreferrer" className="hover:underline">{l.l_product}</a> : l.l_product}
                        {l.l_variant ? <span className="font-normal text-[var(--ink-2)]"> · {l.l_variant}</span> : ''}
                        <span className="font-normal text-[var(--ink-3)]"> × {qty(l.n_qty)}</span>
                      </span>
                      <span className="flex items-center gap-2">
                        {l.n_price_total != null && <span className="num text-xs text-[var(--ink-2)]">{money(l.n_price_total, o.currency)}</span>}
                        {l.l_line_status && <Pill tone={lineTone(l.l_line_status)}>{l.l_line_status}</Pill>}
                      </span>
                    </div>
                    <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-4">
                      <Fact label="In our warehouse" tone={l.qty_in_our_warehouse ? 'font-semibold text-green-700' : 'text-[var(--ink-3)]'}>{`${qty(l.qty_in_our_warehouse || 0)} of ${qty(l.n_qty)}`}</Fact>
                      <Fact label="Shipped">{l.d_shipped ? <>{l.d_shipped}{l.l_tracking_url && <> · <a href={l.l_tracking_url} target="_blank" rel="noreferrer" className="underline">tracking</a></>}</> : (l.n_qty_delivered ? `${qty(l.n_qty_delivered)} delivered` : 'not yet')}</Fact>
                      {!l.d_shipped && <Fact label="Expected in warehouse">{l.d_expected_in_warehouse || (l.f_is_stock_sale ? 'from stock' : 'no date yet')}</Fact>}
                      {!l.d_shipped && <Fact label="Tell the customer">{l.customer_window_warehouse}</Fact>}
                      <Fact label="Expected delivery">{!l.d_shipped && l.d_expected_delivery}</Fact>
                      <Fact label="Checkout promise" tone={l.later_than_promised_at_checkout ? 'text-red-700' : ''}>
                        {l.stock_info_at_checkout && `"${l.stock_info_at_checkout}"`}{l.checkout_promise_date ? ` (until ${l.checkout_promise_date})` : ''}
                        {l.later_than_promised_at_checkout ? ' · passed' : ''}
                      </Fact>
                      <Fact label="Purchase order" tone="text-amber-700">{l.supplier_order === 'not placed yet' ? 'still a draft' : null}</Fact>
                    </dl>
                    {l.supplier_account_internal && <p className="mt-1.5 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900"><b>Supplier (internal):</b> {typeof l.supplier_account_internal === 'string' ? l.supplier_account_internal : JSON.stringify(l.supplier_account_internal)}</p>}
                    {l.customer_timing && <p className="mt-1.5 text-xs text-[var(--ink-2)]"><b>Replai's guidance:</b> {l.customer_timing}</p>}
                    {l.escalate && <p className="mt-1 text-xs font-medium text-amber-800">{l.escalate}</p>}
                  </div>
                </li>
              ))}
            </ul>
            <h3 className="mt-5 text-xs font-semibold uppercase tracking-wide text-[var(--ink-3)]">Shipments</h3>
            {(ship.pickings || []).length ? (
              <ul className="mt-2 divide-y divide-[var(--line-2)] rounded-lg border border-[var(--line)] text-sm">
                {ship.pickings.map((p) => (
                  <li key={p.name} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                    <span className="font-medium">{p.name}</span>
                    <Pill tone={PICK[p.state] || 'bg-[var(--line-2)] text-[var(--ink-2)]'}>{PICK_LABEL[p.state] || p.state}</Pill>
                    <span className="text-[var(--ink-2)]">{p.carrier}</span>
                    {p.date_done && <span className="text-xs text-[var(--ink-3)]">{String(p.date_done).slice(0, 10)}</span>}
                    {(p.tracking_links || []).map((t) => <a key={t.number} href={t.tracking_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-xs underline">{t.number} <ExternalLink size={11} /></a>)}
                    {!(p.tracking_links || []).length && p.tracking_ref && <span className="text-xs">{p.tracking_ref}</span>}
                  </li>))}
              </ul>
            ) : <p className="mt-2 text-xs text-[var(--ink-3)]">No shipment in Odoo.</p>}
            {parcels.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs text-[var(--ink-2)]">
                {parcels.map((p, i) => <li key={i}><b>{p.carrier} {p.tracking_number}:</b> {p.state ? `${p.state}. ` : ''}{p.summary}</li>)}
              </ul>)}
            {ship.carrier_status?.advice && <p className="mt-2 text-xs text-[var(--ink-2)]"><b>Tracking:</b> {ship.carrier_status.advice}</p>}
            {ship.pickup_point_check && ship.pickup_point_check.fits_shippy_limits === false && (
              <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900"><b>Pickup point:</b> {ship.pickup_point_check.carrier}, parcel {ship.pickup_point_check.parcel_cm} cm / {ship.pickup_point_check.parcel_kg} kg is over its limits ({ship.pickup_point_check.limits}).</p>)}
          </>
        )}
      </div>
    </div>
  );
};

const OrderTable = ({ orders, extra }) => {
  const [open, setOpen] = useState(null);
  return (
    <div className="overflow-x-auto rounded-lg border border-[var(--line)] bg-[var(--surface)]">
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-[var(--ink-3)]"><tr>
          <th className="px-3 py-2">Order</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Customer</th>
          <th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Total</th>{extra && <th className="px-3 py-2">{extra.label}</th>}
          <th className="px-3 py-2">Links</th></tr></thead>
        <tbody>{orders.map((o) => (
          <tr key={o.order_id} className="cursor-pointer border-t border-[var(--line-2)] hover:bg-[var(--paper)]" onClick={() => setOpen(o)}>
            <td className="px-3 py-2 font-medium">{o.order_name}</td><td className="px-3 py-2">{(o.order_date || '').slice(0, 10)}</td>
            <td className="max-w-[16rem] truncate px-3 py-2">{o.l_email}</td>
            <td className="px-3 py-2">{o.issue ? <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${ISSUE[o.issue] || ''}`}>{o.issue}</span> : o.l_status}</td>
            <td className="num px-3 py-2 text-right">{money(o.n_price_total, o.currency_id)}</td>
            {extra && <td className="px-3 py-2">{extra.render(o)}</td>}
            <td className="px-3 py-2"><Links o={o} /></td>
          </tr>))}</tbody>
      </table>
      {!orders.length && <p className="p-4 text-sm text-[var(--ink-3)]">Nothing here.</p>}
      {open && <OrderModal order={open} onClose={() => setOpen(null)} />}
    </div>
  );
};

const lateBy = (o) => Number(o.days_overdue !== '' && o.days_overdue != null ? o.days_overdue : o.days_waiting || 0);

const Filters = ({ f, setF, total, shown }) => {
  const sel = 'rounded-md border border-[var(--line)] bg-[var(--surface)] px-2 py-1.5 text-sm';
  return (
    <div className="mb-2 mt-6 flex flex-wrap items-center gap-2">
      <h2 className="mr-2 font-semibold">Needs attention <span className="font-normal text-[var(--ink-3)]">({shown} of {total})</span></h2>
      <select value={f.issue} onChange={(e) => setF({ ...f, issue: e.target.value })} className={sel}>
        <option value="">All issues</option><option value="Overdue delivery">Overdue delivery</option>
        <option value="Waiting on brand (>14d)">Waiting on brand</option>
      </select>
      <select value={f.min} onChange={(e) => setF({ ...f, min: Number(e.target.value) })} className={sel}>
        {[[0, 'Any delay'], [7, 'Late 7+ days'], [14, 'Late 14+ days'], [30, 'Late 30+ days'], [60, 'Late 60+ days']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <select value={f.ticket} onChange={(e) => setF({ ...f, ticket: e.target.value })} className={sel}>
        <option value="">With or without ticket</option><option value="yes">Customer has a ticket</option><option value="no">No ticket yet</option>
      </select>
      <select value={f.sort} onChange={(e) => setF({ ...f, sort: e.target.value })} className={sel}>
        <option value="late">Most late first</option><option value="old">Oldest order first</option><option value="value">Highest value first</option>
      </select>
      <label className="ml-auto flex items-center gap-2 rounded-md border border-[var(--line)] bg-[var(--surface)] px-2.5 py-1.5">
        <Search size={15} className="text-[var(--ink-3)]" />
        <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Order or email" className="w-44 bg-transparent text-sm outline-none" />
      </label>
    </div>
  );
};

const Dashboard = () => {
  const [range, setRange] = useState(90);
  const [d, setD] = useState(null);
  const [f, setF] = useState({ issue: '', min: 0, ticket: '', sort: 'late', q: '' });
  const rows = useMemo(() => {
    const q = f.q.trim().toLowerCase();
    const out = ((d && d.problematic_orders) || []).filter((o) => (!f.issue || o.issue === f.issue) && lateBy(o) >= f.min
      && (!f.ticket || (f.ticket === 'yes' ? o.ticket_id : !o.ticket_id))
      && (!q || `${o.order_name} ${o.l_email}`.toLowerCase().includes(q)));
    const key = { late: (o) => -lateBy(o), old: (o) => o.order_date, value: (o) => -Number(o.n_price_total || 0) }[f.sort];
    return [...out].sort((a, b) => (key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : 0));
  }, [d, f]);
  useEffect(() => {
    setD(null);
    const from = new Date(Date.now() - range * 86400000).toISOString().slice(0, 10);
    axiosInstance.get(API, { params: { view: 'dashboard', date_from: from } }).then(({ data }) => setD(data)).catch(() => setD(false));
  }, [range]);
  return (
    <section className="mt-6">
      <div className="flex flex-wrap items-center gap-1">
        <span className="mr-1 text-sm text-[var(--ink-3)]">Orders placed in the last</span>
        {[30, 90, 180, 365].map((n) => <Chip key={n} active={range === n} onClick={() => setRange(n)}>{n === 365 ? '12 months' : `${n} days`}</Chip>)}
      </div>
      {d === false && <p className="mt-4 text-sm text-red-700">Could not load.</p>}
      {d == null && <div className="mt-4 h-48 animate-pulse rounded-lg bg-[var(--line-2)]" />}
      {d && (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[[d.attention_needed.overdue_delivery, 'overdue deliveries'], [d.attention_needed.later_than_expected, 'items later than expected'],
              [d.attention_needed.ordered_from_brand, 'orders waiting on the brand'], [d.attention_needed.order_placed, 'orders placed, not ordered yet']].map(([v, l]) => (
              <div key={l} className="rounded-lg border border-[var(--line)] bg-[var(--surface)] p-3">
                <div className="num text-2xl font-semibold">{Number(v).toLocaleString('en-GB')}</div><div className="text-sm text-[var(--ink-2)]">{l}</div>
              </div>))}
          </div>
          <Filters f={f} setF={setF} total={d.problematic_orders.length} shown={rows.length} />
          <OrderTable orders={rows}
            extra={{ label: 'Late by', render: (o) => (o.days_overdue !== '' && o.days_overdue != null ? `${o.days_overdue} days` : o.days_waiting ? `${o.days_waiting} days waiting` : '') }} />
        </>
      )}
    </section>
  );
};

const Lookup = () => {
  const [q, setQ] = useState('');
  const [d, setD] = useState(null);
  const go = () => q.trim() && axiosInstance.get(API, { params: { view: 'lookup', q } }).then(({ data }) => setD(data));
  return (
    <section className="mt-6">
      <label className="flex max-w-xl items-center gap-2 rounded-md border border-[var(--line)] bg-[var(--surface)] px-3 py-2">
        <Search size={15} className="text-[var(--ink-3)]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && go()}
          placeholder="Customer email or order number, then Enter" className="flex-1 bg-transparent outline-none" />
      </label>
      {d && <div className="mt-4"><p className="mb-2 text-sm text-[var(--ink-3)]">{d.order_count} order(s). Click an order for its lines.</p><OrderTable orders={d.orders} /></div>}
    </section>
  );
};

const Cleanup = () => {
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);
  useEffect(() => {
    axiosInstance.get(API, { params: { view: 'cleanup', page } }).then(({ data }) => setD(data))
      .catch((e) => setD(e?.response?.status === 403 ? 'denied' : false));
  }, [page]);
  if (d === 'denied') return <p className="mt-6 text-[var(--ink-2)]">Order cleanup is only open to the support leads.</p>;
  if (d === false) return <p className="mt-6 text-[var(--ink-2)]">Could not load. Reload the page to try again.</p>;
  if (!d) return <div className="mt-6 h-48 animate-pulse rounded-lg bg-[var(--line-2)]" />;
  return (
    <section className="mt-6">
      <p className="mb-2 text-sm text-[var(--ink-2)]">{d.total} orders from before 2025 are still open (not shipped or cancelled): most likely to close or cancel in Odoo.</p>
      <OrderTable orders={d.orders} extra={{ label: 'Age', render: (o) => `${o.days_since_order} days` }} />
      <div className="mt-3 flex items-center gap-2 text-sm">
        <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded border border-[var(--line)] px-2 py-1 disabled:opacity-40">Previous</button>
        <span>Page {d.page} of {d.total_pages}</span>
        <button type="button" disabled={page >= d.total_pages} onClick={() => setPage(page + 1)} className="rounded border border-[var(--line)] px-2 py-1 disabled:opacity-40">Next</button>
      </div>
    </section>
  );
};

const TITLES = {
  dashboard: ['Late orders', 'Late orders from the order data, so we can update the customer before they ask.'],
  lookup: ['Order lookup', "A customer's orders by email or order number, with every line's status."],
  cleanup: ['Order cleanup', 'Orders from before 2025 still open in Odoo: likely to close or cancel.'],
};

const OrdersV2 = ({ tab = 'dashboard' }) => (
  <V2Shell>
    <div className="px-6 py-10 sm:px-10 xl:px-14">
      <h1 className="text-[2.4rem] font-semibold leading-[1.1]">{TITLES[tab][0]}</h1>
      <p className="prose-body mt-2 max-w-3xl text-[var(--ink-2)]">{TITLES[tab][1]}</p>
      {tab === 'dashboard' ? <Dashboard /> : tab === 'lookup' ? <Lookup /> : <Cleanup />}
    </div>
  </V2Shell>
);

export default OrdersV2;
